import React, { useState, useRef, useEffect } from 'react';
import { WeatherData, DailyWeather, FishingScore, Coordinates } from '../models/WeatherData';
import { WeatherAPI } from '../services/api';
import { StorageService } from '../services/StorageService';
import { db } from '../services/FishingDB';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';

interface AIRecommendationProps {
  coordinates: Coordinates;
  currentWeather: WeatherData;
  hourlyWeather: WeatherData[];
  dailyWeather: DailyWeather[];
  fishingScore: FishingScore;
  selectedDate: string;
  species: string; // Added: species as prop instead of from settings
}

interface FishingContext {
  fromShore: boolean;
  method: string;
  waterClarity: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Helper function to parse error messages
const parseErrorMessage = (errorText: string, t: any, language: string) => {
  // Try to extract rate limit info from error message
  const rateLimitMatch = errorText.match(/Rate limit exceeded.*?Try again after ([\d-:TZ.]+)/);
  if (rateLimitMatch) {
    const resetTime = new Date(rateLimitMatch[1]);
    const now = new Date();
    const minutesUntilReset = Math.ceil((resetTime.getTime() - now.getTime()) / (1000 * 60));
    
    const message = t.aiRateLimitMessage
      .replace('{minutes}', minutesUntilReset.toString())
      .replace('{plural}', minutesUntilReset === 1 ? '' : 'er');
    
    return {
      type: 'rate-limit',
      title: t.aiRateLimitTitle,
      message: message,
      resetTime: resetTime
    };
  }
  
  // Check for other error types
  if (errorText.includes('429') || errorText.includes('Too Many Requests')) {
    return {
      type: 'rate-limit',
      title: t.aiTooManyRequestsTitle,
      message: t.aiTooManyRequestsMessage,
      resetTime: null
    };
  }
  
  // Generic error
  return {
    type: 'error',
    title: t.aiErrorTitle,
    message: errorText,
    resetTime: null
  };
};

export const AIRecommendation: React.FC<AIRecommendationProps> = ({
  coordinates,
  currentWeather,
  hourlyWeather,
  dailyWeather,
  fishingScore,
  selectedDate
}) => {
  const { t, language } = useLanguage();
  const { defaultSpecies, defaultMethod } = useSettings();
  
  // State for available methods from database
  const [availableMethods, setAvailableMethods] = useState<string[]>(['spin', 'bait', 'trolling', 'fly', 'vertical', 'livescope']);
  
  // State for questionnaire
  const [showIntro, setShowIntro] = useState<boolean>(true);
  const [showQuestionnaire, setShowQuestionnaire] = useState<boolean>(false);
  const [fishingContext, setFishingContext] = useState<FishingContext>({
    fromShore: true,
    method: defaultMethod || 'spin',
    waterClarity: 'moderate'
  });
  
  // State for chat
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [userMessage, setUserMessage] = useState<string>('');
  const [initialResponseComplete, setInitialResponseComplete] = useState<boolean>(false);
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false); // Flag for user interaction
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load available methods from database
  useEffect(() => {
    const loadMethods = async () => {
      try {
        const methods = await db.methods.toArray();
        if (methods.length > 0) {
          const methodKeys = methods.map(m => m.key);
          setAvailableMethods(methodKeys);
        }
      } catch (error) {
        console.error('Failed to load methods:', error);
        // Keep default fallback methods
      }
    };
    loadMethods();
  }, []);

  // Load saved questions and conversation for this location
  useEffect(() => {
    const savedQuestions = StorageService.getLocationQuestions(coordinates.lat, coordinates.lng);
    if (savedQuestions) {
      setFishingContext(savedQuestions);
    } else {
      // Reset to defaults for new location
      setFishingContext({
        fromShore: true,
        method: defaultMethod || 'spin',
        waterClarity: 'moderate'
      });
    }
    
    // Check if there's an existing conversation for this context
    const existingConversation = StorageService.getConversation(
      coordinates.lat,
      coordinates.lng,
      selectedDate,
      defaultSpecies,
      language
    );
    
    if (existingConversation && existingConversation.length > 0) {
      // Load existing conversation and skip intro
      setMessages(existingConversation);
      setShowIntro(false);
      setShowQuestionnaire(false);
      setInitialResponseComplete(true);
      setHasUserInteracted(false); // Don't auto-scroll on page load
    } else {
      // Reset for new conversation
      setShowIntro(true);
      setShowQuestionnaire(false);
      setMessages([]);
      setInitialResponseComplete(false);
      setHasUserInteracted(false); // Reset interaction flag
    }
  }, [coordinates.lat, coordinates.lng, selectedDate, defaultSpecies, language, defaultMethod]);

  // Scroll to bottom when new messages arrive (only if user has interacted)
  useEffect(() => {
    if (hasUserInteracted) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, hasUserInteracted]);

  const startChat = async () => {
    // Save fishing context for this location
    StorageService.saveLocationQuestions(coordinates.lat, coordinates.lng, fishingContext);
    
    setShowIntro(false);
    setShowQuestionnaire(false);
    setLoading(true);
    setError('');
    setHasUserInteracted(true); // Mark that user has started chat
    
    // Create informative waiting message with date and fishing details
    const dateObj = new Date(selectedDate);
    const formattedDate = dateObj.toLocaleDateString(language, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    
    // Get method translation - use translation key if it's a default method
    const methodTranslationKey = `method${fishingContext.method.charAt(0).toUpperCase() + fishingContext.method.slice(1)}` as keyof typeof t;
    const methodText = t[methodTranslationKey] || fishingContext.method; // Fallback to raw key if custom
    
    const waitingMessage = `${t.aiWaitingMessage}

📅 ${formattedDate}
🎣 ${defaultSpecies.charAt(0).toUpperCase() + defaultSpecies.slice(1)}
🎯 ${methodText}${fishingContext.fromShore ? ' (' + t.aiFishingFromShore.replace('?', '') + ')' : ''}

${t.aiAnalyzing}`;
    
    // Show only the waiting message (fishing context is sent to AI but not displayed)
    setMessages([
      { role: 'assistant', content: waitingMessage }
    ]);
    
    // Abort any ongoing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    try {
      let assistantMessage = '';
      
      // Use backend API for AI recommendation
      await WeatherAPI.streamAIRecommendation(
        coordinates,
        currentWeather,
        hourlyWeather,
        dailyWeather,
        fishingScore,
        defaultSpecies,
        selectedDate,
        language,
        (chunk: string) => {
          if (!abortController.signal.aborted) {
            assistantMessage += chunk;
            // Update the assistant message in real-time
            setMessages(prev => {
              const newMessages = [...prev];
              const lastMessage = newMessages[newMessages.length - 1];
              if (lastMessage && lastMessage.role === 'assistant') {
                newMessages[newMessages.length - 1] = {
                  ...lastMessage,
                  content: assistantMessage
                };
              } else {
                newMessages.push({ role: 'assistant', content: assistantMessage });
              }
              return newMessages;
            });
          }
        },
        () => {
          // On complete
          setLoading(false);
          setInitialResponseComplete(true);
        },
        (err: Error) => {
          // On error
          console.error('AI recommendation error:', err);
          if (err.name !== 'AbortError') {
            setError(err.message || 'Could not generate recommendation');
          }
          setLoading(false);
        },
        fishingContext,
        'metric' // TODO: Get from settings
      );
      
      // Save conversation after first response
      setMessages(prev => {
        StorageService.saveConversation(
          coordinates.lat,
          coordinates.lng,
          selectedDate,
          defaultSpecies,
          language,
          prev
        );
        return prev;
      });
    } catch (err: any) {
      if (err.name !== 'AbortError' && !abortController.signal.aborted) {
        console.error('Error fetching AI recommendation:', err);
        setError('Kunde inte hämta AI-rekommendation');
        setLoading(false);
      }
    }
  };

  const sendMessage = async () => {
    if (!userMessage.trim() || loading || !initialResponseComplete) return;
    
    const newUserMessage = userMessage.trim();
    setUserMessage('');
    setMessages(prev => [...prev, { role: 'user', content: newUserMessage }]);
    setLoading(true);
    setError('');
    setHasUserInteracted(true);
    
    try {
      let assistantMessage = '';
      
      // Send chat request to backend with conversation history AND full weather data AND fishing context
      await WeatherAPI.streamAIChat(
        messages.concat([{ role: 'user', content: newUserMessage }]),
        coordinates,
        currentWeather,
        hourlyWeather,
        dailyWeather,
        defaultSpecies,
        selectedDate,
        language,
        (chunk: string) => {
          assistantMessage += chunk;
          // Update the assistant message in real-time
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];
            if (lastMessage && lastMessage.role === 'assistant') {
              newMessages[newMessages.length - 1] = {
                ...lastMessage,
                content: assistantMessage
              };
            } else {
              newMessages.push({ role: 'assistant', content: assistantMessage });
            }
            return newMessages;
          });
        },
        () => {
          // On complete
          setLoading(false);
          
          // Save conversation
          setMessages(current => {
            StorageService.saveConversation(
              coordinates.lat,
              coordinates.lng,
              selectedDate,
              defaultSpecies,
              language,
              current
            );
            return current;
          });
        },
        (err: Error) => {
          // On error
          console.error('Chat error:', err);
          setError(err.message || 'Could not send message');
          setLoading(false);
        },
        fishingContext,
        'metric' // TODO: Get from settings
      );
    } catch (error) {
      console.error('Unexpected chat error:', error);
      setError('Could not send message');
      setLoading(false);
    }
  };

  return (
    <div id="ai-recommendation-section" className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-purple-900/20 to-pink-900/20 backdrop-blur-xl border border-purple-400/20 p-4 sm:p-6">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">🤖</span>
        <h3 className="text-base sm:text-lg font-semibold text-white">
          {t.alfredAI}
        </h3>
        {loading && (
          <div className="ml-auto">
            <div className="relative w-5 h-5">
              <div className="absolute inset-0 border-2 border-purple-400/20 rounded-full"></div>
              <div className="absolute inset-0 border-2 border-transparent border-t-purple-400 rounded-full animate-spin"></div>
            </div>
          </div>
        )}
      </div>

      {error && (() => {
        const parsedError = parseErrorMessage(error, t, language);
        const isRateLimit = parsedError.type === 'rate-limit';
        
        return (
          <div className={`${isRateLimit ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-red-500/10 border-red-500/20'} border rounded-lg p-4 mb-4`}>
            <div className="flex items-start gap-3">
              <span className="text-2xl">{isRateLimit ? '⏱️' : '⚠️'}</span>
              <div className="flex-1 space-y-2">
                <h4 className={`font-semibold ${isRateLimit ? 'text-yellow-300' : 'text-red-300'}`}>
                  {parsedError.title}
                </h4>
                <p className={`text-sm ${isRateLimit ? 'text-yellow-200/80' : 'text-red-200/80'}`}>
                  {parsedError.message}
                </p>
                {parsedError.resetTime && (
                  <p className="text-xs text-slate-400 mt-1">
                    {t.aiRateLimitResetAt} {parsedError.resetTime.toLocaleTimeString(language, { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {showIntro ? (
        <div className="space-y-6">
          <div className="text-center space-y-4">
            <h4 className="text-xl font-bold text-white">{t.aiWelcomeTitle}</h4>
            <div className="space-y-2">
              <p className="text-slate-300">
                {t.aiWelcomeIntro} <span className="font-semibold text-cyan-300">
                  {new Date(selectedDate).toLocaleDateString(language, { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
              </p>
              {new Date(selectedDate) < new Date() && (
                <p className="text-purple-300 text-sm italic">{t.aiWelcomePastDate}</p>
              )}
            </div>
            <div className="bg-white/5 border border-purple-400/10 rounded-lg p-4">
              <p className="text-slate-300 text-sm leading-relaxed">
                {t.aiWelcomeHistorical}
              </p>
            </div>
          </div>

          <button
            onClick={() => {
              setShowIntro(false);
              setShowQuestionnaire(true);
            }}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl"
          >
            {t.aiStartQuestions}
          </button>
        </div>
      ) : showQuestionnaire ? (
        <div className="space-y-6">
          <p className="text-slate-300 text-sm">{t.aiQuestionIntro}</p>
          
          {/* Question 1: From Shore */}
          <div className="space-y-2">
            <label className="text-purple-300 font-medium text-sm">{t.aiFishingFromShore}</label>
            <div className="flex gap-2">
              <button
                onClick={() => setFishingContext(prev => ({ ...prev, fromShore: true }))}
                className={`flex-1 px-4 py-2 rounded-lg transition-all ${
                  fishingContext.fromShore
                    ? 'bg-purple-500/30 border-2 border-purple-400 text-white'
                    : 'bg-white/5 border-2 border-white/10 text-slate-300 hover:bg-white/10'
                }`}
              >
                {t.aiYes}
              </button>
              <button
                onClick={() => setFishingContext(prev => ({ ...prev, fromShore: false }))}
                className={`flex-1 px-4 py-2 rounded-lg transition-all ${
                  !fishingContext.fromShore
                    ? 'bg-purple-500/30 border-2 border-purple-400 text-white'
                    : 'bg-white/5 border-2 border-white/10 text-slate-300 hover:bg-white/10'
                }`}
              >
                {t.aiNo}
              </button>
            </div>
          </div>

          {/* Question 2: Fishing Method */}
          <div className="space-y-2">
            <label className="text-purple-300 font-medium text-sm">{t.aiFishingMethod}</label>
            <div className="grid grid-cols-3 gap-2">
              {availableMethods.map(method => {
                // Get translation key for method
                const translationKey = `method${method.charAt(0).toUpperCase() + method.slice(1)}` as keyof typeof t;
                const methodLabel = t[translationKey] || method;
                
                return (
                  <button
                    key={method}
                    onClick={() => setFishingContext(prev => ({ ...prev, method }))}
                    className={`px-3 py-2 rounded-lg text-sm transition-all ${
                      fishingContext.method === method
                        ? 'bg-purple-500/30 border-2 border-purple-400 text-white'
                        : 'bg-white/5 border-2 border-white/10 text-slate-300 hover:bg-white/10'
                    }`}
                  >
                    {methodLabel}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question 3: Water Clarity */}
          <div className="space-y-2">
            <label className="text-purple-300 font-medium text-sm">{t.aiWaterClarity}</label>
            <div className="grid grid-cols-3 gap-2">
              {['clear', 'moderate', 'murky'].map(clarity => (
                <button
                  key={clarity}
                  onClick={() => setFishingContext(prev => ({ ...prev, waterClarity: clarity }))}
                  className={`px-3 py-2 rounded-lg text-sm transition-all ${
                    fishingContext.waterClarity === clarity
                      ? 'bg-purple-500/30 border-2 border-purple-400 text-white'
                      : 'bg-white/5 border-2 border-white/10 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  {t[`aiClarity${clarity.charAt(0).toUpperCase() + clarity.slice(1)}` as keyof typeof t]}
                </button>
              ))}
            </div>
          </div>

          {/* Start Button */}
          <button
            onClick={startChat}
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? `${t.aiAnalyzing}` : t.aiStartChat}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Chat Messages */}
          <div className="bg-white/5 rounded-lg border border-purple-400/10 p-4 space-y-4">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] rounded-lg p-3 ${
                    msg.role === 'user'
                      ? 'bg-purple-500/30 text-white'
                      : 'bg-white/10 text-slate-200'
                  }`}
                >
                  <div className="text-sm leading-relaxed whitespace-pre-wrap">
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}
            {loading && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex justify-start">
                <div className="bg-white/10 rounded-lg p-3">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Chat Input */}
          <div className="flex gap-2">
            <input
              type="text"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && !loading && initialResponseComplete && sendMessage()}
              placeholder={initialResponseComplete ? t.aiTypingPlaceholder : t.aiWaitTime}
              disabled={loading || !initialResponseComplete}
              className="flex-1 bg-white/5 border border-purple-400/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-purple-400/50 disabled:opacity-50"
            />
            <button
              onClick={sendMessage}
              disabled={loading || !userMessage.trim() || !initialResponseComplete}
              className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t.aiSendMessage}
            </button>
          </div>

          <div className="flex items-center gap-2 text-xs text-purple-400/60">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>{t.aiCacheInfo}</span>
          </div>
        </div>
      )}
    </div>
  );
};
